/**
 * wayai evals — list evaluation scenarios for the current hub, or query eval
 * results stored in ClickHouse.
 *
 * Usage:
 *   wayai evals              # list all evals
 *   wayai evals --enabled    # show only enabled evals
 *   wayai evals --disabled   # show only disabled evals
 *
 *   wayai evals sql "SELECT count() FROM conversation"
 *   wayai evals sql "SELECT eval_id, count() FROM conversation GROUP BY 1" --json
 *   wayai evals sql --schema                                  # column + eval-score-path catalog
 *   wayai evals sql "..." --limit 500
 *
 * `sql` is a thin wrapper over POST /api/evals/sql — server enforces hub_id +
 * is_eval=true scoping (CTE shadow), single-SELECT only, function/keyword
 * denylist, 10k row cap. `--schema` calls GET /api/evals/sql/schema and
 * returns top-level columns + system variable paths + dynamically discovered
 * `data.eval_scores.*` dimensions.
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { printQueryRows, printSqlSchema, queryWithSchemaRefusal } from '../lib/sql-output.js';
import { parsePositiveIntFlag } from '../lib/utils.js';

export async function evalsCommand(args: string[]): Promise<void> {
  if (args[0] === 'sql') {
    return evalsSqlSubcommand(args.slice(1));
  }
  return evalsListCommand(args);
}

// ---------------------------------------------------------------------------
// Default list command (GET /api/evals)
// ---------------------------------------------------------------------------

async function evalsListCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  let enabledFilter: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--enabled') {
      enabledFilter = 'true';
    } else if (arg === '--disabled') {
      enabledFilter = 'false';
    }
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const result = await client.getEvals(hubId, { enabled: enabledFilter });

  if (result.data.evals.length === 0) {
    console.log('No evals found.');
    return;
  }

  console.log(`Evals (${result.data.evals.length}):\n`);

  for (const eval_ of result.data.evals) {
    const status = eval_.enabled ? 'enabled' : 'disabled';
    const runs = `${eval_.number_of_runs} run${eval_.number_of_runs === 1 ? '' : 's'}`;
    const agentName = eval_.agent?.agent_name || 'unknown';
    console.log(`  ${eval_.eval_name.padEnd(20)}  ${eval_.eval_id.slice(0, 8)}  ${status.padEnd(9)}  ${runs.padEnd(7)}  Agent: ${agentName}`);
  }
}

// ---------------------------------------------------------------------------
// `wayai evals sql` — POST /api/evals/sql (read-only, server-scoped to is_eval = true)
// ---------------------------------------------------------------------------

async function evalsSqlSubcommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);

  let sql: string | undefined;
  let limit: number | undefined;
  let jsonOutput = false;
  let schemaOnly = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--limit') {
      limit = parsePositiveIntFlag('--limit', args[++i]);
    } else if (arg === '--json') {
      jsonOutput = true;
    } else if (arg === '--schema') {
      schemaOnly = true;
    } else if (!arg.startsWith('--') && sql === undefined) {
      sql = arg;
    } else {
      console.error(`Unknown flag for \`evals sql\`: ${arg}`);
      process.exit(1);
    }
  }

  if (schemaOnly && sql !== undefined) {
    console.error(queryWithSchemaRefusal('wayai evals sql'));
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  requireRepoConfig();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (schemaOnly) {
    const schema = await client.getEvalsSqlSchema(hubId);
    if (jsonOutput) {
      console.log(JSON.stringify(schema.data, null, 2));
      return;
    }
    printSqlSchema(schema.data, 'Variable paths (data.system.* + data.eval_scores.*):');
    return;
  }

  if (!sql) {
    console.error('Provide a SQL string. Example: wayai evals sql "SELECT count() FROM conversation"');
    console.error('Or run: wayai evals sql --schema   to list available columns and paths.');
    process.exit(1);
  }

  const result = await client.runEvalsSql({
    hub_id: hubId,
    sql,
    ...(limit !== undefined && { limit }),
  });

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  printQueryRows(result.data);
}
